import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDemo } from "@/store/DemoContext";
import { Crest } from "@/components/Crest";
import { StateBadge } from "@/components/StateBadge";
import { CountdownBadge } from "@/components/CountdownBadge";
import { fmtTime, fmtScore, fmtNumber } from "@/lib/format";
import { userAggregates, userRank } from "@/lib/standings";
import { scoreMeta } from "@/lib/scoring";
import { ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";

export default function Home() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { dataset, state, set, getState, remainingMs, setFixtureState } = useDemo();
  const { language, scoringMode } = state;
  const profile = dataset.profiles[state.profileId];

  const [leagueId, setLeagueId] = useState(state.activeLeagueId || "elite");
  const league = dataset.leagues.find((l) => l.id === leagueId);
  const [mdIdx, setMdIdx] = useState(dataset.liveMatchday - 1); // 0-based

  const md = league.matchdays[mdIdx];
  const isLiveMd = md.isLive;

  const agg = userAggregates(scoringMode, profile.history);
  const rank = userRank(scoringMode, dataset, profile);
  const meta = scoreMeta(scoringMode);

  const nextLock = useMemo(() => {
    if (!isLiveMd) return null;
    let min = null;
    md.fixtures.forEach((f) => {
      if (getState(f) === "OPEN") {
        const r = remainingMs(f);
        if (r != null && r > 0 && (min == null || r < min)) min = r;
      }
    });
    return min;
  }, [md, isLiveMd, getState, remainingMs]);

  const openFixture = (f) => {
    set("activeFixtureId", f.id);
    set("activeLeagueId", leagueId);
    const s = getState(f);
    if (s === "SETTLED") nav(`/result/${f.id}`);
    else if (s === "REVEALED" || s === "IN_PLAY") nav(`/compare/${f.id}`);
    else nav(`/build/${f.id}`);
  };

  const empty = profile.history.length === 0;

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 py-5 space-y-5">
      {/* Season strip */}
      <div data-testid="season-strip" className="rounded-2xl border border-neutral-800 bg-gradient-to-r from-neutral-900 to-neutral-950 p-4 sm:p-5">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-display">{t("home.seasonSummary")}</div>
            {empty ? (
              <div className="mt-1 text-neutral-400 text-sm max-w-xs">{t("home.emptyHistory")}</div>
            ) : (
              <div className="mt-1 flex items-baseline gap-3">
                <span className="font-display text-4xl sm:text-5xl font-bold tabular" style={{ color: "#b6ff2e" }} data-testid="season-strip-score">
                  {fmtScore(scoringMode, agg.score, language)}
                </span>
                <span className="text-xs text-neutral-500 pb-1">{t(meta.labelKey)} · {meta.betterIsLower ? t("common.lowerIsBetter") : t("common.higherIsBetter")}</span>
              </div>
            )}
          </div>
          <div className="flex gap-4 sm:gap-6">
            <Stat label={t("common.rank")} value={rank ? `#${rank.rank}` : t("home.unranked")} />
            <Stat label={t("home.vsCoach")} value={`${agg.vsCoach}/${agg.played}`} color="#ffb547" />
            <Stat label={t("home.vsAi")} value={`${agg.vsAi}/${agg.played}`} color="#3fd7e5" />
          </div>
        </div>
      </div>

      {/* league tabs + matchday nav */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1 p-1 rounded-xl bg-neutral-900 border border-neutral-800" data-testid="league-tabs">
          {dataset.leagues.map((l) => (
            <button key={l.id} data-testid={`league-tab-${l.id}`}
              onClick={() => setLeagueId(l.id)}
              className={`px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                leagueId === l.id ? "bg-[#b6ff2e] text-black" : "text-neutral-400 hover:text-neutral-100"
              }`}>
              {l.name}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2" data-testid="matchday-nav">
          <button disabled={mdIdx === 0} onClick={() => setMdIdx((i) => Math.max(0, i - 1))}
            className="p-1.5 rounded-lg border border-neutral-800 disabled:opacity-30 hover:bg-neutral-900"><ChevronLeft size={16} /></button>
          <div className="font-display uppercase text-sm tracking-wide min-w-[110px] text-center">
            {t("common.matchday")} {md.matchday} {isLiveMd && <span className="text-[#b6ff2e]">· Live</span>}
          </div>
          <button disabled={mdIdx === league.matchdays.length - 1} onClick={() => setMdIdx((i) => Math.min(league.matchdays.length - 1, i + 1))}
            className="p-1.5 rounded-lg border border-neutral-800 disabled:opacity-30 hover:bg-neutral-900"><ChevronRight size={16} /></button>
        </div>
      </div>

      {/* next lock banner */}
      {nextLock != null && (
        <div className="flex items-center justify-between rounded-xl border border-[#b6ff2e]/20 bg-[#b6ff2e]/5 px-4 py-3">
          <span className="text-sm text-neutral-300 font-display uppercase tracking-wide">{t("home.nextLock")}</span>
          <CountdownBadge ms={nextLock} testid="home-next-lock" />
        </div>
      )}

      {/* fixtures grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 stagger" data-testid="fixtures-grid">
        {md.fixtures.map((f) => (
          <FixtureCard key={f.id} fixture={f} onOpen={openFixture} interactive={isLiveMd}
            state={getState(f)} language={language} scoringMode={scoringMode} dataset={dataset}
            submission={state.submissions[f.id]} draft={state.drafts[f.id]} remainingMs={remainingMs(f)} />
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, color = "#e5e5e5" }) {
  return (
    <div className="text-right">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-display">{label}</div>
      <div className="font-display text-xl font-bold tabular" style={{ color }}>{value}</div>
    </div>
  );
}

function FixtureCard({ fixture, onOpen, interactive, state, language, scoringMode, dataset, submission, draft }) {
  const { t } = useTranslation();
  const home = dataset.clubsById[fixture.homeId];
  const away = dataset.clubsById[fixture.awayId];

  let status = t("status.notEntered");
  if (interactive) {
    if (submission) status = t("status.submitted");
    else if (draft) status = t("status.draftSaved");
  }

  const settled = state === "SETTLED";
  const showScore = settled || state === "IN_PLAY";

  return (
    <button data-testid={`fixture-card-${fixture.id}`}
      onClick={() => interactive && onOpen(fixture)}
      disabled={!interactive}
      className={`text-left rounded-2xl border border-neutral-800 bg-neutral-900/60 p-4 transition-all ${
        interactive ? "hover:border-[#b6ff2e]/40 hover:-translate-y-0.5 cursor-pointer" : "opacity-70 cursor-default"
      }`}>
      <div className="flex items-center justify-between mb-3">
        <StateBadge state={state} testid={`fixture-state-${fixture.id}`} />
        <span className="text-[11px] text-neutral-500 tabular">{fmtTime(fixture.kickoff, language)}</span>
      </div>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Crest club={home} size={30} />
          <span className="font-display font-semibold text-sm truncate">{home.name}</span>
        </div>
        {showScore ? (
          <span className="font-display text-2xl font-bold tabular px-2">{fixture.finalScore.home}–{fixture.finalScore.away}</span>
        ) : (
          <span className="text-neutral-600 font-display text-sm px-2">{t("common.vs")}</span>
        )}
        <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
          <span className="font-display font-semibold text-sm truncate text-right">{away.name}</span>
          <Crest club={away} size={30} />
        </div>
      </div>
      {interactive && (
        <div className="mt-3 pt-3 border-t border-neutral-800 flex items-center justify-between">
          <span className="text-xs text-neutral-400">{status}</span>
          <span className="flex items-center gap-1 text-xs font-medium text-[#b6ff2e]">
            {settled ? t("home.viewResult") : (state === "REVEALED" || state === "IN_PLAY") ? t("home.viewComparison") : t("home.buildXI")}
            <ArrowUpRight size={14} />
          </span>
        </div>
      )}
    </button>
  );
}
